import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Globe, Save, Upload, Store, Loader2, Camera, Trash2, RefreshCw, CheckCircle2, AlertTriangle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Reusable "publish this material to the webshop" panel used on the Material detail page.
export function WebshopPanel({ material, onSaved }) {
  const code = material.material_code;
  const [enabled, setEnabled] = useState(!!material.webshop_enabled);
  const [title, setTitle] = useState(material.webshop_title || material.name || "");
  const [price, setPrice] = useState(material.retail_price ?? material.std_price ?? 0);
  const [customers, setCustomers] = useState(material.webshop_customers || ["28732"]);
  const [groups, setGroups] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [hasImage, setHasImage] = useState(!!material.image_path);

  useEffect(() => { api.get("/pim/customer-groups").then((r) => setGroups(r.data.groups || [])).catch(() => {}); }, []);

  const toggleCust = (c) => setCustomers((cur) => (cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c]));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/materials/${encodeURIComponent(code)}`, {
        webshop_enabled: enabled, webshop_title: title, retail_price: Number(price),
        webshop_customers: customers,
      });
      toast.success(enabled ? "Published to webshop" : "Webshop settings saved");
      onSaved && onSaved();
    } catch (e) { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/materials/${encodeURIComponent(code)}/webshop-image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setHasImage(true); toast.success("Image uploaded"); onSaved && onSaved();
    } catch (e) { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  return (
    <div className="rounded-lg border border-[#e5e9f0] bg-white p-5" data-testid="mat-webshop-panel">
      <div className="mb-4 flex items-center gap-2">
        <Globe size={18} className="text-[#7c3aed]" />
        <h3 className="text-sm font-semibold text-[#32363a]">Webshop / Portal</h3>
        <span className={`ml-2 rounded px-2 py-0.5 text-[10px] font-medium ${enabled ? "bg-[#e6f4ea] text-[#137333]" : "bg-[#f0f1f3] text-[#6a6d70]"}`}>{enabled ? "Live on portal" : "Not published"}</span>
      </div>

      <div className="grid gap-5 md:grid-cols-2">
        <div className="grid gap-4">
          <label className="flex items-center gap-3 rounded-md border border-[#e5e9f0] px-3 py-2.5">
            <input data-testid="ws-enabled" type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="h-4 w-4" />
            <div><div className="text-sm font-medium text-[#32363a]">Enable on portal</div><div className="text-xs text-[#6a6d70]">Show this product to enabled customers in the webshop</div></div>
          </label>
          <div><div className="text-[11px] uppercase tracking-wide text-[#6a6d70]">Storefront title</div>
            <input data-testid="ws-title" value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 w-full rounded-md border border-[#c8cfda] px-3 py-2 text-sm outline-none focus:border-[#7c3aed]" /></div>
          <div><div className="text-[11px] uppercase tracking-wide text-[#6a6d70]">Retail price (£)</div>
            <input data-testid="ws-price" type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} className="mt-1 w-full rounded-md border border-[#c8cfda] px-3 py-2 text-sm outline-none focus:border-[#7c3aed]" />
            <div className="mt-1 text-[10px] text-[#9aa0a6]">Live: price &amp; stock always reflect this material master. Free stock: {material.free_stock ?? 0} {material.uom}</div></div>
        </div>
        <div className="grid gap-4">
          <div>
            <div className="mb-1 flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-[#6a6d70]"><Store size={12} /> Visible to customer groups</div>
            <div className="space-y-1.5">
              {(groups.length ? groups : [{ code: "28732", label: "Marks & Spencer" }]).map((g) => (
                <label key={g.code} className="flex items-center gap-2 rounded-md border border-[#e5e9f0] px-3 py-2 text-sm text-[#32363a]">
                  <input data-testid={`ws-cust-${g.code}`} type="checkbox" checked={customers.includes(g.code)} onChange={() => toggleCust(g.code)} className="h-4 w-4" />
                  {g.label} <span className="font-mono text-[10px] text-[#9aa0a6]">{g.code}</span>
                </label>
              ))}
            </div>
            <div className="mt-1 text-[10px] text-[#9aa0a6]">Only selected customer groups see this product when they log into the portal.</div>
          </div>
          <div>
            <div className="mb-1 text-[11px] uppercase tracking-wide text-[#6a6d70]">Product image</div>
            <div className="flex items-center gap-3">
              {hasImage ? <img src={`${API}/api/shop/media/${material.id}?t=${Date.now()}`} alt="" className="h-16 w-16 rounded-md border border-[#e5e9f0] object-contain" />
                : <div className="flex h-16 w-16 items-center justify-center rounded-md border border-dashed border-[#c8cfda] text-[#c1c6cd]"><Upload size={18} /></div>}
              <label className="cursor-pointer rounded-md border border-[#c8cfda] px-3 py-2 text-xs font-medium text-[#32363a] hover:border-[#7c3aed]">
                {uploading ? <span className="flex items-center gap-1.5"><Loader2 size={13} className="animate-spin" /> Uploading…</span> : (hasImage ? "Replace image" : "Upload image")}
                <input data-testid="ws-image" type="file" accept="image/*" className="hidden" onChange={uploadImage} />
              </label>
            </div>
          </div>
        </div>
      </div>

      <RefPhotos materialId={material.id} />

      <div className="mt-5 flex justify-end">
        <button data-testid="ws-save" onClick={save} disabled={saving}
          className="inline-flex items-center gap-1.5 rounded-md bg-[#7c3aed] px-4 py-2 text-sm font-semibold text-white hover:bg-[#6d28d9] disabled:opacity-50">
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save webshop settings
        </button>
      </div>
    </div>
  );
}

function RefPhotos({ materialId }) {
  const [imgs, setImgs] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/pim/materials/${materialId}/reference-images`).then((r) => setImgs(r.data.images || [])).catch(() => {});
  useEffect(() => { load(); }, [materialId]);
  const upload = async (e) => {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    setBusy(true);
    try {
      const fd = new FormData(); files.forEach((f) => fd.append("files", f));
      const { data } = await api.post(`/pim/materials/${materialId}/reference-images`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImgs(data.images || []); toast.success(`${data.added} reference photo(s) added`);
    } catch (err) { toast.error("Upload failed"); } finally { setBusy(false); e.target.value = ""; }
  };
  const del = async (id) => { try { await api.delete(`/pim/reference-images/${id}`); setImgs((cur) => cur.filter((x) => x.id !== id)); } catch (err) { toast.error("Delete failed"); } };
  const [rebuilding, setRebuilding] = useState(false);
  const rebuild = async () => {
    setRebuilding(true);
    try {
      const { data } = await api.post(`/pim/visual-index/rebuild`);
      toast.success(`Scan index refreshed · ${data.indexed_products} products recognisable`);
    } catch (err) { toast.error("Rebuild failed"); } finally { setRebuilding(false); }
  };
  const [test, setTest] = useState(null);
  const [testing, setTesting] = useState(false);
  const runTest = async (e) => {
    const f = (e.target.files || [])[0]; if (!f) return;
    setTesting(true); setTest(null);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post(`/pim/materials/${materialId}/scan-test`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setTest(data);
    } catch (err) { toast.error("Test failed"); } finally { setTesting(false); e.target.value = ""; }
  };
  return (
    <div className="mt-5 rounded-md border border-[#e5e9f0] p-4" data-testid="ref-photos">
      <div className="mb-1 flex items-center gap-2"><Camera size={15} className="text-[#7c3aed]" /><span className="text-sm font-semibold text-[#32363a]">Scan reference photos</span></div>
      <p className="mb-3 text-xs text-[#6a6d70]">Upload several angles of this product. Buyers scanning with their camera are matched to the nearest catalogue items — fully offline, catalogue-only. The product must be published to the shop (enabled for the buyer's group) to appear in scan results.</p>
      <div className="flex flex-wrap items-center gap-3">
        {imgs.map((im) => (
          <div key={im.id} className="relative h-20 w-20 overflow-hidden rounded-md border border-[#e5e9f0] bg-white" title={im.origin === "buyer-confirm" ? `Buyer scan by ${im.added_by || "buyer"}` : "Admin upload"}>
            <img src={`${API}/api/pim/ref-media/${im.id}?w=200`} alt="" className="h-full w-full object-contain" />
            {im.origin === "buyer-confirm" && <span data-testid={`ref-buyer-${im.id}`} className="absolute bottom-0 left-0 right-0 bg-[#F06600]/90 py-0.5 text-center text-[9px] font-semibold text-white">Buyer scan</span>}
            <button data-testid={`ref-del-${im.id}`} onClick={() => del(im.id)} title="Remove" className="absolute right-0 top-0 rounded-bl bg-black/60 p-1 text-white hover:bg-[#bb0000]"><Trash2 size={12} /></button>
          </div>
        ))}
        <label className="flex h-20 w-20 cursor-pointer flex-col items-center justify-center gap-1 rounded-md border border-dashed border-[#c8cfda] text-[#9aa0a6] hover:border-[#7c3aed]">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <><Upload size={16} /><span className="text-[10px]">Add</span></>}
          <input data-testid="ref-upload" type="file" accept="image/*" multiple className="hidden" onChange={upload} />
        </label>
      </div>
      <button type="button" data-testid="rebuild-scan-index" onClick={rebuild} disabled={rebuilding}
        className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-[#c8cfda] px-3 py-1.5 text-xs font-semibold text-[#7c3aed] hover:border-[#7c3aed] disabled:opacity-50">
        {rebuilding ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Rebuild scan recognition index
      </button>
      <div className="mt-3 border-t border-[#eef0f4] pt-3">
        <label data-testid="scan-test-label" className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-[#c8cfda] px-3 py-1.5 text-xs font-semibold text-[#32363a] hover:border-[#7c3aed]">
          {testing ? <Loader2 size={13} className="animate-spin" /> : <Camera size={13} />} Test match with a photo
          <input data-testid="scan-test-input" type="file" accept="image/*" className="hidden" onChange={runTest} />
        </label>
        {test && (
          <div data-testid="scan-test-result" className={`mt-2 rounded-md border p-3 text-xs ${test.would_appear ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}>
            <div className="mb-1.5 flex items-center gap-1.5 font-semibold">
              {test.would_appear
                ? <><CheckCircle2 size={14} className="text-emerald-600" /> This product will appear when scanned</>
                : <><AlertTriangle size={14} className="text-amber-600" /> This product will NOT appear when scanned</>}
            </div>
            <ul className="space-y-0.5 text-[#4a4f55]">
              <li>{test.fingerprints > 0 ? "✓" : "✗"} Fingerprints on file: <b>{test.fingerprints}</b>{test.fingerprints === 0 && " — add Scan reference photos or re-save the product image"}</li>
              <li>{test.enabled ? "✓" : "✗"} Published to shop: <b>{test.enabled ? "Yes" : "No"}</b></li>
              <li className="flex flex-wrap items-center gap-1">Published to groups:
                {(test.group_status || []).map((g) => (
                  <span key={g.code} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${g.in ? "bg-emerald-100 text-emerald-700" : "bg-[#eef0f4] text-[#89919a]"}`}>{g.in ? "✓" : "✗"} {g.label}</span>
                ))}
              </li>
              <li>{test.rank ? "✓" : "✗"} This photo ranks it: <b>{test.rank ? `#${test.rank} (${test.score})` : "not in top matches"}</b></li>
              <li className="mt-1 text-[11px] text-[#6a6d70]">A buyer only sees it when scanning as a group ticked above. You are testing as <b>{test.your_group}</b>.</li>
            </ul>
            {test.top?.length > 0 && (
              <div className="mt-2 border-t border-black/5 pt-1.5">
                <div className="mb-0.5 text-[10px] uppercase tracking-wide text-[#89919a]">Top matches for this photo</div>
                {test.top.map((t, i) => (
                  <div key={i} className="flex justify-between gap-2"><span className="truncate">{i + 1}. {t.title} <span className="text-[#89919a]">({t.code})</span></span><span className="shrink-0 font-mono">{t.score}{!t.enabled || !t.in_your_group ? " ⚠" : ""}</span></div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


// Admin review of every buyer "This is it" scan photo, with one-click removal.
export function BuyerScanReview() {
  const [photos, setPhotos] = useState(null);
  useEffect(() => { api.get("/pim/buyer-scan-photos").then((r) => setPhotos(r.data.photos || [])).catch(() => setPhotos([])); }, []);
  const del = async (id) => {
    try { await api.delete(`/pim/reference-images/${id}`); setPhotos((cur) => cur.filter((x) => x.id !== id)); toast.success("Scan photo removed"); }
    catch (err) { toast.error("Delete failed"); }
  };
  if (!photos) return <div className="flex justify-center py-8"><Loader2 className="animate-spin text-[#9aa0a6]" /></div>;
  if (!photos.length) return <div data-testid="scan-review-empty" className="py-8 text-center text-sm text-[#6a6d70]">No buyer scan photos yet.</div>;
  return (
    <div data-testid="scan-review-list" className="grid max-h-[60vh] grid-cols-2 gap-3 overflow-y-auto sm:grid-cols-3 md:grid-cols-4">
      {photos.map((p) => (
        <div key={p.id} data-testid={`scan-review-${p.id}`} className="overflow-hidden rounded-lg border border-[#e5e9f0] bg-white">
          <div className="grid grid-cols-2 gap-px bg-[#e5e9f0]">
            <img src={`${API}/api/pim/ref-media/${p.id}?w=200`} alt="Buyer scan" className="aspect-square w-full bg-white object-cover" />
            {p.has_image ? <img src={`${API}/api/shop/media/${p.material_id}?w=200`} alt="Catalogue" className="aspect-square w-full bg-white object-contain" />
              : <div className="flex aspect-square items-center justify-center bg-[#f5f6f7] text-[10px] text-[#9aa0a6]">No catalogue photo</div>}
          </div>
          <div className="space-y-1 p-2">
            <div className="line-clamp-2 text-xs font-semibold text-[#32363a]">{p.title}</div>
            <div className="font-mono text-[10px] text-[#9aa0a6]">{p.material_code} · {p.added_by || "buyer"}{p.added_at ? ` · ${new Date(p.added_at).toLocaleDateString("en-GB")}` : ""}</div>
            <button data-testid={`scan-review-del-${p.id}`} onClick={() => del(p.id)} className="inline-flex w-full items-center justify-center gap-1 rounded-md border border-[#f3c4c4] py-1 text-xs font-semibold text-[#bb0000] hover:bg-[#fdecec]"><Trash2 size={12} /> Remove</button>
          </div>
        </div>
      ))}
    </div>
  );
}
